import React, { PropTypes } from 'react';
import { connect } from 'react-redux';
import DateRangePicker from '../../../../tc-ui/date-range-picker';
import { seasonColors } from '../../../../../utils/contracting-preset-names-codes';
import DateUtils from '../../../../../utils/date-utils';
import StartEndDate from './start-end-date';
import SelectSeasonName from '../../../generic-components/select-season-name';
import R from 'ramda';
import moment from 'moment';

const _convertDateRanges = dateRanges => dateRanges.map(dateRange => ({
  from: DateUtils._getDateFromUTC(dateRange.seasonStartDate),
  to: DateUtils._getDateFromUTC(dateRange.seasonEndDate),
  _id: dateRange._id,
}));

const EditSeason = ({
  existingSeasons,
  onChange,
  seasonIndex,
  selectedSeasonName,
  selectedDateRanges,
  contractingPeriod,
}) => {
  const dateRanges = _convertDateRanges(selectedDateRanges);
  const renderDay = R.curry((rangePicker, day) => {
    const foundSeasonIndex = R.findIndex(R.compose(DateUtils.dayInDateRanges(day), R.prop('dateRanges')), existingSeasons);
    return (
      <div
        style={{
          get color() {
            if (foundSeasonIndex === seasonIndex && rangePicker) return 'white';
            if (foundSeasonIndex + 1 > seasonColors.length) return 'grey';
            return seasonColors[foundSeasonIndex];
          },
          fontWeight: foundSeasonIndex < 0 ? '500' : '600',
        }}
      >
        {day.getDate()}
      </div>
    );
  });
  const otherSeasons = R.remove(seasonIndex, 1, existingSeasons);
  const onStartEndUpdate = index => newRange => {
    const restOfSeasons = existingSeasons.map((existingSeason, i) => {
      if (i === seasonIndex) {
        return R.merge(existingSeason, {
          dateRanges: R.remove(index, 1, existingSeason.dateRanges),
        });
      }
      return existingSeason;
    });
    if (
      DateUtils.overlapExisting(
        { from: newRange.seasonStartDate, to: newRange.seasonEndDate },
        restOfSeasons,
      )
    ) {
      const adjustRestOfSeasons = DateUtils.removeOverlapDates(
        { from: newRange.seasonStartDate, to: newRange.seasonEndDate },
        restOfSeasons,
      );
      const adjustedExistingSeasons = adjustRestOfSeasons.map((existingSeason, i) => {
        if (i === seasonIndex) {
          return R.merge(existingSeason, {
            dateRanges: R.insert(index, newRange, existingSeason.dateRanges),
          });
        }
        return existingSeason;
      });
      onChange({ keyPath: ['data', 'existingSeasons'], value: adjustedExistingSeasons });
    } else {
      onChange({
        keyPath: ['data', 'existingSeasons', seasonIndex, 'dateRanges', index],
        value: newRange,
      });
    }
  };
  const onDeleteDR = i => () => {
    onChange({
      keyPath: ['data', 'existingSeasons', seasonIndex, 'dateRanges'],
      value: R.remove(i, 1, selectedDateRanges),
    });
  };
  const onAddDR = i => () => {
    onChange({
      keyPath: ['data', 'existingSeasons', seasonIndex, 'dateRanges'],
      value: !selectedDateRanges.length
        ? [{}, {}]
        : R.insert(i + 1, {}, selectedDateRanges),
    })
  };
  return (
    <div style={{ paddingTop: '16px', width: 'calc(100% - 200px)' }}>
      <SelectSeasonName
        style={{ marginBottom: '12px', width: '460px', fontSize: '13px' }}
        selectedSeasonName={selectedSeasonName}
        contractingPeriod={contractingPeriod}
        existingSeasons={existingSeasons}
        onChange={value => onChange({ keyPath: ['data', 'existingSeasons', seasonIndex, 'seasonName'], value })}
      />
      <small><i>Please select from the calendar or enter dates directly.</i></small>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexDirection: 'row-reverse', marginTop: '8px' }}>
        <div style={{ marginTop: '12px', width: '400px' }}>
          {
            (selectedDateRanges.length ? selectedDateRanges : [{}]).map((range, index) => {
              const overlapping = existingSeasons[seasonIndex].overlappingDates && DateUtils.overlapExisting({ from: range.seasonStartDate, to: range.seasonEndDate }, otherSeasons);
              return (
                <div key={`${index}${seasonIndex}`}>
                  <StartEndDate
                    index={index}
                    prevDREnd={
                      R.pathOr(null, [index - 1, 'seasonEndDate'], selectedDateRanges)
                      || R.pathOr(null, [index - 1, 'seasonStartDate'], selectedDateRanges)
                    }
                    renderDay={renderDay}
                    selectedSeasonName={selectedSeasonName}
                    seasonIndex={seasonIndex}
                    contractingPeriod={contractingPeriod}
                    range={range}
                    onChange={onChange}
                    onUpdate={onStartEndUpdate(index)}
                    onDelete={onDeleteDR(index)}
                    onAdd={onAddDR(index)}
                  />
                  { overlapping ? <small style={{ color: 'red' }}>This date range is overlapping with another season.</small> : <small/> }
                </div>
              );
            })
          }
          {
            !selectedDateRanges.length && (
              <small style={{ color: '#f5372a' }}>Please enter at least one valid date range for this season.</small>
            )
          }
        </div>
        <div style={{ paddingRight: '16px', width: '566px' }}>
          <DateRangePicker
            noShowSelected
            numberOfMonths={6}
            startDate={contractingPeriod.startDate}
            endDate={contractingPeriod.endDate}
            selectedRanges={dateRanges}
            initialMonth={dateRanges.length
              ? DateUtils.getFirstDayFromAnArrayOfRanges(dateRanges.filter(dr => dr.from))
              : new Date(contractingPeriod.startDate.getUTCFullYear(), contractingPeriod.startDate.getUTCMonth(), contractingPeriod.startDate.getUTCDate())}
            renderDay={renderDay(true)}
            onConfirmClick={range => {
              const newRange = {
                seasonStartDate: moment.utc(range.from).format(),
                seasonEndDate: range.to ? moment.utc(range.to).format() : range.to
              };
              const newDateRangesForTheSeason = (() => {
                // for when user click the end date of a range
                if (
                  selectedDateRanges.length
                  && moment(R.last(selectedDateRanges).seasonStartDate).isSame(range.from)
                ) {
                  return [...R.dropLast(1, selectedDateRanges), newRange];
                }
                return R.append(newRange, selectedDateRanges);
              })();
              if (DateUtils.overlapExisting(range, existingSeasons)) {
                const adjustedExistingSeasons = DateUtils.removeOverlapDates(range, existingSeasons);
                onChange({
                  keyPath: ['data', 'existingSeasons'],
                  value: adjustedExistingSeasons.map((season, i) => i === seasonIndex ? R.merge(season, { dateRanges: newDateRangesForTheSeason }) : season),
                });
              } else {
                onChange({
                  keyPath: ['data', 'existingSeasons', seasonIndex, 'dateRanges'],
                  value: newDateRangesForTheSeason,
                });
              }
            }}
          />
        </div>
      </div>
    </div>);
};


EditSeason.propTypes = {
  onRequestClose: PropTypes.func,
  onTextFieldChange: PropTypes.func,
  contractId: PropTypes.string,
  productId: PropTypes.string,
  saveProductInfoChanges: PropTypes.func,
  data: PropTypes.object,
  takenDateRanges: PropTypes.array,
  saving: PropTypes.bool,
};
const mapStateToProps = ({ contracting }) => ({
  dialogOpen: contracting.addEditContractDialog.get('open'),
});

export default connect(mapStateToProps)(EditSeason);



// WEBPACK FOOTER //
// ./src/app/components/contracting/contract-activities-products/add-edit-dialog-content/edit-seasons/edit-season.jsx